import { useState, useEffect } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { AdminAlert, AdminButton, isNotBuiltYet } from '../AdminShared'
import {
  adminGetPolicies,
  adminUpdatePolicy,
  adminAddPolicyCategory,
  adminDeletePolicyCategory,
} from '../../../services/admin'

const CONTENT_FALLBACK = {
  terms_of_service: `Article 1: Purpose\nThis Terms of Service aims to regulate the rights, obligations, and responsibilities between the Company and users regarding the use of the online community service provided by "Chicago Community" (hereinafter referred to as the "Service").\n\nArticle 2: Effectiveness and Amendment of Terms\nThese Terms shall take effect by being posted on the Service screen or notified through other methods. The Company may amend these Terms to the extent that it does not violate relevant laws and regulations, and will provide prior notice upon amendment. Users who do not agree to the amended Terms may discontinue use of the Service and withdraw their membership.\n\nArticle 3: Definitions\n"User" refers to a member who uses the Service in accordance with these Terms. "Member" refers to a person who has registered with the Service, received a user account, and uses the Service. "Post" means text, comments, images, links, etc., posted by users on the Service.`,
  privacy_policy: `Article 1: Personal Information Collected\nUpon membership registration: Email, password, nickname, IP address, device information. Automatically collected during service use: Access logs, IP address, browser information, usage records.\n\nArticle 2: Purposes of Collection and Use of Personal Information\nMember identification and login authentication; Service provision and community operation; Prevention of fraudulent use and security enhancement.`,
  youth_protection: `Article 1: Purpose\nThis policy aims to establish measures to block harmful information and provide protection so that adolescents can use the service safely.\n\nArticle 2: Blocking Harmful Information\nThe Company prohibits the following harmful content: Obscene or sexual content; Content that promotes violence, suicide, or self-harm; Information inducing drugs, gambling, or illegal activities.`,
}

const DEFAULT_CATEGORIES = [
  { key: 'terms_of_service', label: 'Terms of Service', icon: 'gavel' },
  { key: 'privacy_policy', label: 'Privacy Policy', icon: 'privacy_tip' },
  { key: 'youth_protection', label: 'Youth Protection', icon: 'child_care' },
]

const ICON_SUGGESTIONS = ['description', 'gavel', 'privacy_tip', 'child_care', 'shield', 'cookie', 'policy', 'verified_user']

export function PolicyEditor() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('policies')
  const [categories, setCategories]   = useState(DEFAULT_CATEGORIES)
  const [activeKey, setActiveKey]     = useState('terms_of_service')
  const [drafts, setDrafts]           = useState({})   // key → edited text
  const [saved, setSaved]             = useState({})   // key → saved text from API
  const [loading, setLoading]         = useState(true)
  const [saving, setSaving]           = useState(false)
  const [error, setError]             = useState(null)
  const [successKey, setSuccessKey]   = useState(null)
  const [apiAvailable, setApiAvailable] = useState(null) // null=unknown, true/false

  // Add-category form
  const [showAddForm, setShowAddForm] = useState(false)
  const [newLabel, setNewLabel]       = useState('')
  const [newIcon, setNewIcon]         = useState('description')
  const [addingCategory, setAddingCategory] = useState(false)
  const [addError, setAddError]       = useState(null)

  const [deletingKey, setDeletingKey] = useState(null)

  const loadPolicies = () => {
    setLoading(true)
    adminGetPolicies()
      .then(res => {
        const data = res?.data ?? res ?? {}
        const cats = Array.isArray(data.categories) && data.categories.length
          ? data.categories
          : DEFAULT_CATEGORIES
        setApiAvailable(true)
        setCategories(cats)
        const initial = {}
        cats.forEach(c => {
          initial[c.key] = data.content?.[c.key] ?? CONTENT_FALLBACK[c.key] ?? ''
        })
        setSaved(initial)
        setDrafts(initial)
        setActiveKey(prev => (cats.some(c => c.key === prev) ? prev : cats[0]?.key))
      })
      .catch(err => {
        setApiAvailable(false)
        const initial = {}
        DEFAULT_CATEGORIES.forEach(c => { initial[c.key] = CONTENT_FALLBACK[c.key] })
        setSaved(initial)
        setDrafts(initial)
        if (!isNotBuiltYet(err)) setError(err.message)
      })
      .finally(() => setLoading(false))
  }

  useEffect(loadPolicies, [])

  const isDirty = drafts[activeKey] !== saved[activeKey]
  const activeMeta = categories.find(c => c.key === activeKey)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await adminUpdatePolicy(activeKey, drafts[activeKey])
      setSaved(prev => ({ ...prev, [activeKey]: drafts[activeKey] }))
      setSuccessKey(activeKey)
      setTimeout(() => setSuccessKey(null), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setDrafts(prev => ({ ...prev, [activeKey]: saved[activeKey] }))
  }

  const handleAddCategory = async () => {
    if (!newLabel.trim()) return
    setAddingCategory(true)
    setAddError(null)
    try {
      const updated = await adminAddPolicyCategory(newLabel.trim(), newIcon || 'description')
      const cats = Array.isArray(updated?.data) ? updated.data : (Array.isArray(updated) ? updated : null)
      if (cats) {
        setCategories(cats)
        const added = cats[cats.length - 1]
        setDrafts(prev => ({ ...prev, [added.key]: '' }))
        setSaved(prev => ({ ...prev, [added.key]: '' }))
        setActiveKey(added.key)
      } else {
        loadPolicies()
      }
      setNewLabel('')
      setNewIcon('description')
      setShowAddForm(false)
    } catch (err) {
      setAddError(err.message)
    } finally {
      setAddingCategory(false)
    }
  }

  const handleDeleteCategory = async (key) => {
    if (categories.length <= 1) return
    if (!window.confirm(`Remove "${categories.find(c => c.key === key)?.label}"? This can't be undone.`)) return
    setDeletingKey(key)
    setError(null)
    try {
      const updated = await adminDeletePolicyCategory(key)
      const cats = Array.isArray(updated?.data) ? updated.data : (Array.isArray(updated) ? updated : null)
      if (cats) {
        setCategories(cats)
        if (activeKey === key) setActiveKey(cats[0]?.key)
      } else {
        loadPolicies()
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setDeletingKey(null)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Policy tab switcher — wraps onto multiple rows on narrow screens
          instead of squeezing every tab into an ever-shrinking horizontal
          scroll strip, so it stays usable as more categories get added. */}
      <div className="flex flex-wrap gap-2">
        {categories.map(c => (
          <div key={c.key} className="relative group">
            <button
              onClick={() => { setActiveKey(c.key); setError(null) }}
              className={`flex items-center gap-2 py-3 pl-4 pr-9 font-bold text-[11px] uppercase tracking-widest border-4 transition-all
                ${activeKey === c.key
                  ? 'bg-on-surface text-surface border-on-surface'
                  : 'bg-surface text-on-surface border-on-surface hover:bg-surface-container'}`}
            >
              <span className="material-symbols-outlined text-[15px]" style={{ fontVariationSettings: "'FILL' 1" }}>{c.icon}</span>
              <span>{c.label}</span>
              {drafts[c.key] !== saved[c.key] && (
                <span className="w-2 h-2 rounded-full bg-primary-container inline-block" title="Unsaved changes" />
              )}
            </button>
            {canEdit && categories.length > 1 && (
              <button
                onClick={() => handleDeleteCategory(c.key)}
                disabled={deletingKey === c.key}
                title={`Remove ${c.label}`}
                className={`absolute right-1.5 top-1/2 -translate-y-1/2 md:opacity-0 md:group-hover:opacity-100 transition-opacity
                  ${activeKey === c.key ? 'text-surface/70 hover:text-surface' : 'text-on-surface-variant hover:text-error'}`}
              >
                <span className="material-symbols-outlined text-[16px]">
                  {deletingKey === c.key ? 'hourglass_empty' : 'close'}
                </span>
              </button>
            )}
          </div>
        ))}

        {/* Add category */}
        {canEdit && <button
          onClick={() => setShowAddForm(v => !v)}
          className="flex items-center gap-2 py-3 px-4 font-bold text-[11px] uppercase tracking-widest border-4 border-dashed border-on-surface-variant/40 text-on-surface-variant hover:border-on-surface hover:text-on-surface transition-all"
        >
          <span className="material-symbols-outlined text-[15px]">add</span>
          Add category
        </button>}
      </div>

      {canEdit && showAddForm && (
        <div className="border-4 border-on-surface bg-surface p-4 flex flex-col gap-3" style={{ boxShadow: '4px 4px 0px 0px #000' }}>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="Category name, e.g. Community Guidelines"
              className="flex-1 px-4 py-3 border-4 border-on-surface bg-surface text-on-surface text-sm font-medium focus:outline-none focus:border-primary"
            />
            <select
              value={newIcon}
              onChange={e => setNewIcon(e.target.value)}
              className="px-4 py-3 border-4 border-on-surface bg-surface text-on-surface text-sm font-medium focus:outline-none focus:border-primary sm:w-56"
            >
              {ICON_SUGGESTIONS.map(icon => (
                <option key={icon} value={icon}>{icon}</option>
              ))}
            </select>
          </div>
          {addError && <AdminAlert type="error" message={addError} />}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <AdminButton onClick={handleAddCategory} loading={addingCategory} disabled={!newLabel.trim()} variant="gold">
                <span className="material-symbols-outlined text-[16px]">add</span>
                Create category
              </AdminButton>
            </div>
            <div className="sm:w-32">
              <AdminButton onClick={() => setShowAddForm(false)} variant="outline">Cancel</AdminButton>
            </div>
          </div>
        </div>
      )}

      {error && <AdminAlert type="error" message={error} />}
      {successKey === activeKey && activeMeta && <AdminAlert type="success" message={`${activeMeta.label} saved successfully.`} />}

      {apiAvailable === false && (
        <AdminAlert
          type="info"
          message="Backend policy endpoint not live yet — edits will save locally in this session but won't persist until the backend is deployed."
        />
      )}

      {/* Editor */}
      {activeMeta && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
              {activeMeta.icon}
            </span>
            <span className="font-extrabold text-[13px] uppercase tracking-wider">{activeMeta.label}</span>
          </div>

          {loading ? (
            <div className="h-[420px] bg-surface border-4 border-on-surface animate-pulse" />
          ) : (
            <textarea
              value={drafts[activeKey] ?? ''}
              onChange={e => setDrafts(prev => ({ ...prev, [activeKey]: e.target.value }))}
              rows={22}
              readOnly={!canEdit}
              spellCheck={false}
              className="w-full px-5 py-4 border-4 border-on-surface bg-surface text-on-surface font-mono text-[12px] leading-relaxed placeholder:text-on-surface-variant/40 focus:ring-0 focus:outline-none focus:border-primary transition-colors resize-y"
              style={{ boxShadow: '4px 4px 0px 0px #000' }}
              placeholder="Policy content…"
            />
          )}

          <p className="text-[11px] text-on-surface-variant font-medium">
            Use double line breaks to separate paragraphs. Lines starting with "Article N:" are rendered as section headers on the public page.
          </p>
        </div>
      )}

      {/* Actions */}
      {canEdit && activeMeta && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <AdminButton
              onClick={handleSave}
              loading={saving}
              disabled={!isDirty || loading}
              variant="gold"
            >
              <span className="material-symbols-outlined text-[16px]">save</span>
              Save {activeMeta.label}
            </AdminButton>
          </div>
          <div className="sm:w-40">
            <AdminButton
              onClick={handleReset}
              disabled={!isDirty || loading || saving}
              variant="outline"
            >
              Discard
            </AdminButton>
          </div>
        </div>
      )}
    </div>
  )
}